import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FaSave, FaTimes } from 'react-icons/fa';
import { updateBookingStatus } from '../services/customerService';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const showToast = (type, message) => {
  const configs = {
    success: {
      color: "#28a745",
      border: "2px solid #28a745",
    },
    error: {
      color: "#dc3545",
      border: "2px solid #dc3545",
    },
    warning: {
      color: "#ffc107",
      border: "2px solid #ffc107",
    },
    info: {
      color: "#17a2b8",
      border: "2px solid #17a2b8",
    },
  };

  const config = configs[type];

  toast[type](message, {
    position: "top-right",
    autoClose: 3000,
    transition: Bounce,
    style: {
      border: config.border,
      background: "transparent",
      color: config.color,
      boxShadow: "none",
      marginTop: "70px",
    },
    icon: false,
  });
};

const statusOptions = [
  { value: 'booked', label: 'Booked' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rescheduled', label: 'Rescheduled' },
];

const EditBookingModal = ({ show, onHide, booking, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    if (booking) {
      setStatus(booking.booking_status || '');
      setRemarks('');
    }
  }, [booking]);

  useEffect(() => {
    if (!show) {
      setStatus('');
      setRemarks('');
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!status) {
      showToast('warning', 'Please select a status');
      return;
    }

    setLoading(true);

    try {
      console.log('🔄 Updating booking status:', {
        bookingId: booking.id,
        bookingNumber: booking.booking_number,
        newStatus: status,
        remarks: remarks,
      });

      await updateBookingStatus(booking.id, {
        status,
        remarks,
        changed_by: 1,
      });

      console.log('✅ Booking status updated successfully!');
      showToast('success', `Booking status updated to ${statusOptions.find(s => s.value === status)?.label || status}! 🎉`);

      if (onSuccess) {
        onSuccess();
      }

      setTimeout(() => {
        onHide();
      }, 500);

    } catch (error) {
      console.error('❌ Update booking status error:', error.response?.data || error);
      showToast('error', error.response?.data?.message || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStatusLabel = () => {
    if (!booking?.booking_status) return 'Unknown';
    const found = statusOptions.find(s => s.value === booking.booking_status);
    return found?.label || booking.booking_status;
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        transition={Bounce}
      />

      <Modal show={show} onHide={onHide} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Booking Status</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Booking #</Form.Label>
                  <Form.Control
                    type="text"
                    value={booking?.booking_number || 'N/A'}
                    disabled
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Customer</Form.Label>
                  <Form.Control
                    type="text"
                    value={booking?.customer_name || 'N/A'}
                    disabled
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Cylinder Type</Form.Label>
                  <Form.Control
                    type="text"
                    value={booking?.cylinder_type_name || booking?.cylinder_type || 'N/A'}
                    disabled
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Current Status</Form.Label>
                  <Form.Control
                    type="text"
                    value={getCurrentStatusLabel()}
                    disabled
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>New Status *</Form.Label>
                  <Form.Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    required
                  >
                    <option value="">Select Status</option>
                    {statusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                  {!status && (
                    <Form.Text className="text-danger">
                      Please select a status
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Add remarks (optional)..."
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={onHide}
              style={{ borderRadius: '30px', padding: '10px 24px' }}
            >
              <FaTimes className="me-2" /> Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !status}
              style={{
                backgroundColor: "rgb(30, 58, 111)",
                border: "none",
                borderRadius: "30px",
                padding: "10px 24px",
              }}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <><FaSave className="me-2" /> Update Status</>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default EditBookingModal;